/**
 * /wireline-slickline/invoices/:id — one INV- invoice on its own page
 * (fieldiq-api#1359, WL-5). Opened from the register, New invoice, a job's
 * Invoices panel or a ticket's "On invoice" link. The back arrow returns to the
 * register with its filters. Well Servicing's page (WS-5), carried over,
 * Mark paid included.
 *
 * EVERY FIGURE IS THE API'S, AS STORED AT GENERATION — lines, subtotal, each
 * levied tax (its stored label and amount), the tax's provenance and the total
 * come from GET /invoices/:id, the loader the PDF and the send also use.
 * Nothing here adds lines up or computes tax. An absent figure reads `—`.
 *
 * ACTIONS (band, top right; admin and office staff — everyone else reads):
 *   draft              PDF · Send · Void
 *   sent / approved    PDF · Send again · Mark paid · Void
 *   paid / void        PDF. Final and read-only.
 * Mark paid goes through Finance's door (PATCH /unified-invoices/:id). Each action
 * opens INLINE under the band (InvoiceActions.tsx), no modal. When the company
 * default priced the tax (`taxRateSource` company_columns), the page says why;
 * a fallback the send refuses (`tax_fallback_blocked`) holds Send and says what
 * to do instead.
 */
import React, { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { AlertTriangle, Ban, CheckCircle2, FileText, Send } from 'lucide-react';
import { api, getBlobApiErrorMessage } from '../../../api/client';
import { PageHeaderBand } from '../../../components/layout/PageHeaderBand';
import { LoadFailureNotice } from '../../../components/ui/LoadFailureNotice';
import { useAuth } from '../../../contexts/AuthContext';
import { useTenantTimeZone } from '../../../hooks/useTenantTimeZone';
import { useWlInvoice } from '../../../hooks/useWlInvoices';
import { claimBlobTab, POPUP_BLOCKED_MESSAGE, showBlobInTab } from '../../../lib/blob-tab';
import { formatInstant } from '../../../lib/calendar-date';
import { fmtCurrency } from '../../../lib/display-labels';
import { queryFailureError, queryLoadFailure } from '../../../lib/load-failure';
import { fmtWlNumber } from '../../../lib/wireline-slickline';
import { wlDay } from '../../../lib/wireline-slickline-jobs';
import { wlScopeText } from '../../../lib/wireline-slickline-tickets';
import {
  canWriteWlInvoices,
  wlInvoiceCanMarkPaid,
  wlInvoiceIsFinal,
  wlInvoiceIsLive,
  wlInvoiceIsTaxFallback,
  wlInvoiceLineGroups,
  wlInvoiceSendBlocked,
  wlInvoiceStatusSkin,
  wlLeviedTaxRows,
  wlReleasedTicketNumbers,
  wlTaxFallbackText,
  wlTaxRowLabel,
  wlTaxSourceText,
  WL_FINANCE_INVOICES_PATH,
} from '../../../lib/wireline-slickline-invoices';
import type { WlInvoice, WlInvoiceLine } from '../../../types/wireline-slickline-invoices';
import { useBackToList } from '../../downhole/recordPage';
import { wlRateText } from '../tickets/TicketBits';
import { isNotFound, WL_BAND_SECONDARY, WL_EYEBROW, WL_PRIMARY, WlBadge, WlEmpty, WlFormError, WlLoading, WlPanel, WlSpec } from '../WlBits';
import { WlInvoiceMarkPaidPanel, WlInvoiceSendPanel, WlInvoiceVoidPanel, WlReleasedNotice } from './InvoiceActions';

const at = (v: string | null | undefined, tz: string | null) => (v ? formatInstant(v, tz) ?? '—' : '—');

/**
 * A line's quantity with its unit, as the ticket's lines read it
 * (TicketLines): `1,250 m · Measured depth`, `12 Per shot · Per hole`,
 * `42 km`, `10.5 Per hour`. Absent → `—`.
 */
export const wlInvoiceQtyText = (l: Pick<WlInvoiceLine, 'quantity' | 'unit' | 'unitLabel' | 'depthUnit' | 'depthBasisLabel' | 'shotBasisLabel' | 'distanceUnit'>) => {
  const v = fmtWlNumber(l.quantity);
  if (v === '—') return v;
  let u: string | null;
  if (l.unit === 'per_depth') u = [l.depthUnit, l.depthBasisLabel].filter(Boolean).join(' · ') || l.unitLabel || l.unit;
  else if (l.unit === 'per_shot') u = [l.unitLabel ?? 'Per shot', l.shotBasisLabel].filter(Boolean).join(' · ');
  else if (l.unit === 'per_distance') u = l.distanceUnit ?? l.unitLabel ?? l.unit;
  else u = l.unitLabel ?? l.unit;
  return u ? `${v} ${u}` : v;
};

/** Said when the company default priced the tax: amber when the send refuses it, neutral when it is benign. */
export function WlTaxFallbackNotice({ inv }: { inv: WlInvoice }) {
  const text = wlTaxFallbackText(inv);
  if (!text) return null;
  const blocked = wlInvoiceSendBlocked(inv);
  return (
    <div
      role="note"
      data-testid="wl-invoice-tax-fallback"
      data-blocked={blocked ? 'true' : 'false'}
      className={`flex items-start gap-3 rounded-fi-panel border border-fieldiq-border border-l-4 bg-fieldiq-card px-4 py-3 text-sm text-fieldiq-text ${blocked ? 'border-l-fi-amber' : 'border-l-fi-steel'}`}
    >
      {blocked && <AlertTriangle className="mt-0.5 size-4 shrink-0 text-fi-signal-amber" aria-hidden="true" />}
      <div>
        <p className="font-bold">{blocked ? 'This invoice cannot be sent.' : 'Tax from the company settings.'}</p>
        <p className="mt-1">{text}</p>
      </div>
    </div>
  );
}

export default function WlInvoiceDetailScreen() {
  const { id } = useParams<{ id: string }>();
  const back = useBackToList(WL_FINANCE_INVOICES_PATH);
  const { user, company } = useAuth();
  const showsPst = company?.country === 'CA';
  const tz = useTenantTimeZone();
  const query = useWlInvoice(id);
  const inv = query.data?.data ?? null;
  const failure = queryLoadFailure(query);
  const notFound = !inv && !query.isLoading && (isNotFound(queryFailureError(query)) || !failure);
  const [panel, setPanel] = useState<'send' | 'paid' | 'void' | null>(null);
  const [released, setReleased] = useState<string[] | null>(null);
  const [pdfBusy, setPdfBusy] = useState(false);
  const [pdfError, setPdfError] = useState<string | null>(null);

  const canWrite = canWriteWlInvoices(user?.role);
  const status = inv?.status ?? null;
  const live = wlInvoiceIsLive(status);
  const sendBlocked = !!inv && wlInvoiceSendBlocked(inv);
  const cur = inv?.currency ?? null;

  const openPdf = async () => {
    if (!inv) return;
    setPdfError(null);
    const win = claimBlobTab();
    if (!win) return setPdfError(POPUP_BLOCKED_MESSAGE);
    setPdfBusy(true);
    try {
      const res = await api.wirelineSlickline.invoices.pdf(inv.id);
      showBlobInTab(win, res.data);
    } catch (e) {
      win.close();
      setPdfError(await getBlobApiErrorMessage(e, 'The PDF could not be made.'));
    } finally {
      setPdfBusy(false);
    }
  };

  const actions = inv ? (
    <div className="flex flex-wrap gap-2" data-testid="wl-invoice-actions">
      <button type="button" className={WL_BAND_SECONDARY} onClick={openPdf} disabled={pdfBusy} data-testid="wl-invoice-pdf">
        <FileText className="size-4" aria-hidden="true" />
        {pdfBusy ? 'Opening…' : 'PDF'}
      </button>
      {canWrite && live && (
        <button type="button" className={WL_BAND_SECONDARY} onClick={() => setPanel('void')} data-testid="wl-invoice-void">
          <Ban className="size-4" aria-hidden="true" />
          Void
        </button>
      )}
      {canWrite && wlInvoiceCanMarkPaid(status) && (
        <button type="button" className={WL_BAND_SECONDARY} onClick={() => setPanel('paid')} data-testid="wl-invoice-mark-paid">
          <CheckCircle2 className="size-4" aria-hidden="true" />
          Mark paid
        </button>
      )}
      {canWrite && live && !sendBlocked && (
        <button type="button" className={WL_PRIMARY} onClick={() => setPanel('send')} data-testid="wl-invoice-send">
          <Send className="size-4" aria-hidden="true" />
          {inv.sentAt ? 'Send again' : 'Send'}
        </button>
      )}
    </div>
  ) : undefined;

  return (
    <div data-testid="wl-invoice-page" className="animate-fade-in min-h-full bg-fieldiq-dark">
      <PageHeaderBand
        eyebrow={WL_EYEBROW}
        title={inv?.invoiceNumber || (query.isLoading ? 'Invoice' : '—')}
        description={inv ? `${inv.customerName ?? '—'} · ${inv.jobNumber ?? (inv.jobId ? '—' : 'Several jobs')}` : undefined}
        onBack={back}
        backLabel="Back to invoices"
        actions={actions}
      />
      <section className="px-[var(--fi-page-inline)] py-[var(--fi-page-block)] pb-[var(--bottom-chrome)]">
        <div className="mx-auto max-w-[1920px] space-y-4" data-testid="wl-invoice-detail">
          {!inv && query.isLoading ? (
            <WlLoading label="Loading the invoice" />
          ) : notFound ? (
            <WlPanel eyebrow="Invoice" title="Invoice">
              <WlEmpty>This invoice was not found.</WlEmpty>
            </WlPanel>
          ) : !inv ? (
            <WlPanel eyebrow="Invoice" title="Invoice">
              <LoadFailureNotice error={queryFailureError(query)} kind={failure ?? 'unreachable'} subject="this invoice" />
            </WlPanel>
          ) : (
            <>
              <WlFormError message={pdfError} />
              {released && <WlReleasedNotice numbers={wlReleasedTicketNumbers(inv, released)} onDismiss={() => setReleased(null)} />}
              {status === 'void' && (
                <div role="status" data-testid="wl-invoice-void-note" className="rounded-fi-panel border border-fieldiq-border border-l-4 border-l-fi-red bg-fieldiq-card px-4 py-3 text-sm text-fieldiq-text">
                  <p className="font-bold">Void {at(inv.voidedAt, tz)}{inv.voidedByName ? ` by ${inv.voidedByName}` : ''}. This invoice is final and read-only.</p>
                  <p className="mt-1 whitespace-pre-wrap">{inv.voidReason || '—'}</p>
                </div>
              )}
              {status === 'paid' && (
                <p role="status" data-testid="wl-invoice-paid-note" className="rounded-fi-panel border border-fieldiq-border border-l-4 border-l-fi-green bg-fieldiq-card px-4 py-3 text-sm text-fieldiq-text">
                  Paid {at(inv.paidAt, tz)}{inv.paidByName ? `, recorded by ${inv.paidByName}` : ''}. This invoice is final and read-only.
                </p>
              )}
              {!wlInvoiceIsFinal(status) && wlInvoiceIsTaxFallback(inv) && <WlTaxFallbackNotice inv={inv} />}
              {canWrite && live && panel === 'send' && !sendBlocked && <WlInvoiceSendPanel inv={inv} tz={tz} onDone={() => setPanel(null)} />}
              {canWrite && wlInvoiceCanMarkPaid(status) && panel === 'paid' && <WlInvoiceMarkPaidPanel inv={inv} onDone={() => setPanel(null)} />}
              {canWrite && live && panel === 'void' && <WlInvoiceVoidPanel inv={inv} onDone={() => setPanel(null)} onVoided={setReleased} />}

              <WlPanel eyebrow="Invoice" title="Details" testId="wl-invoice-details">
                <dl className="grid gap-4 px-4 py-4 sm:grid-cols-2 lg:grid-cols-4">
                  <WlSpec label="Status" value={<span data-testid="wl-invoice-status"><WlBadge skin={wlInvoiceStatusSkin(status)} value={status} /></span>} />
                  <WlSpec label="Invoice date" value={wlDay(inv.invoiceDate)} />
                  <WlSpec label="Due date" value={wlDay(inv.dueDate)} />
                  <WlSpec label="Currency" value={inv.currency || '—'} />
                  <WlSpec label="Job" value={inv.jobId ? <Link className="underline" to={`/wireline-slickline/jobs/${inv.jobId}?tab=tickets`}>{inv.jobNumber || '—'}</Link> : 'Several jobs'} mono />
                  <WlSpec label="Well" value={inv.wellName || '—'} />
                  <WlSpec label={inv.country === 'US' ? 'API number' : 'UWI'} value={inv.wellIdentifier || '—'} mono />
                  <WlSpec label="Surface location" value={inv.surfaceLocation || '—'} mono />
                  <WlSpec label="Province / state" value={inv.jurisdiction || '—'} />
                  <WlSpec label="PO number" value={inv.poNumber || '—'} mono />
                  <WlSpec label="AFE number" value={inv.afeNumber || '—'} mono />
                  <WlSpec label="Cost code" value={inv.costCode || '—'} mono />
                  <WlSpec label="Created" value={`${at(inv.createdAt, tz)}${inv.createdByName ? ` by ${inv.createdByName}` : ''}`} />
                  <WlSpec label="Last sent" value={inv.sentAt ? `${at(inv.sentAt, tz)} to ${inv.sentTo || '—'}` : 'Not sent'} />
                  {inv.sentCc && inv.sentCc.length > 0 && <WlSpec label="Copied to" value={inv.sentCc.join(', ')} />}
                  {showsPst && <WlSpec label="PST exemption" value={inv.pstExempt ? inv.pstExemptNumber || 'Exempt' : '—'} />}
                </dl>
                {inv.notes && <p className="whitespace-pre-wrap border-t border-fieldiq-border px-4 py-3 text-sm text-fieldiq-text">{inv.notes}</p>}
              </WlPanel>

              <WlPanel eyebrow="Bill to" title={inv.customerName || '—'} testId="wl-invoice-customer">
                <dl className="grid gap-4 px-4 py-4 sm:grid-cols-2 lg:grid-cols-4">
                  <WlSpec label="Billing address" value={inv.customerAddress || '—'} />
                  <WlSpec label="Main contact" value={inv.mainContactName || '—'} />
                  <WlSpec label="Main contact email" value={inv.mainContactEmail || '—'} />
                  <WlSpec label="Accounting email" value={inv.accountingEmail || '—'} />
                </dl>
              </WlPanel>

              <WlPanel eyebrow="Billed" title="Field tickets" testId="wl-invoice-tickets">
                {inv.tickets.length === 0 ? (
                  <WlEmpty>This invoice lists no field tickets.</WlEmpty>
                ) : (
                  <ul className="divide-y divide-fieldiq-border">
                    {inv.tickets.map((t) => (
                      <li key={t.id} className="flex flex-wrap items-center gap-x-4 gap-y-1 px-4 py-3 text-sm" data-testid="wl-invoice-ticket">
                        <Link to={`/wireline-slickline/tickets/${t.id}`} className="font-mono font-extrabold text-white underline underline-offset-2">
                          {t.ticketNumber}
                        </Link>
                        <span className="font-mono text-fieldiq-text">{t.jobNumber || '—'}</span>
                        <span className="text-fieldiq-text">{wlScopeText({ scope: t.scope, workDate: t.workDate })}</span>
                        <span className="text-fieldiq-muted">Approved by {t.customerApprovedByName || '—'}</span>
                        <span className="font-mono text-fieldiq-muted">PO {t.poNumber || '—'}</span>
                        <span className="font-mono text-fieldiq-text">{fmtCurrency(t.subtotal, cur)} before tax</span>
                        {t.releasedAt && <span className="text-fieldiq-muted">Released {at(t.releasedAt, tz)}</span>}
                      </li>
                    ))}
                  </ul>
                )}
              </WlPanel>

              <WlPanel eyebrow="Charges" title="Lines" testId="wl-invoice-lines">
                {inv.lines.length === 0 ? (
                  <WlEmpty>This invoice has no lines.</WlEmpty>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full min-w-[680px] text-left">
                      <thead className="border-b border-fieldiq-border bg-fieldiq-dark/40 text-[0.64rem] font-black uppercase tracking-[0.12em] text-fieldiq-muted">
                        <tr className="border-l-4 border-l-transparent">
                          <th scope="col" className="px-4 py-3">Charge</th>
                          <th scope="col" className="px-4 py-3 text-right">Qty</th>
                          <th scope="col" className="px-4 py-3 text-right">Rate</th>
                          <th scope="col" className="px-4 py-3 text-right">Amount</th>
                        </tr>
                      </thead>
                      {wlInvoiceLineGroups(inv).map((g) => {
                        const t = inv.tickets.find((x) => x.id === g.ticketId);
                        return (
                          <tbody key={g.ticketId} data-testid="wl-invoice-line-group">
                            <tr className="border-t border-l-4 border-fieldiq-border border-l-transparent bg-fieldiq-dark/30">
                              <th scope="colgroup" colSpan={4} className="px-4 py-2 text-left text-xs font-bold text-fieldiq-text">
                                <span className="font-mono">{g.ticketNumber}</span>
                                {t ? ` · ${wlScopeText({ scope: t.scope, workDate: t.workDate })}` : ''}
                                {` · Approved by ${t?.customerApprovedByName || '—'}`}
                              </th>
                            </tr>
                            {g.lines.map((l) => (
                              <tr key={l.id} data-testid="wl-invoice-line" className="border-t border-l-4 border-fieldiq-border border-l-transparent">
                                <td className="px-4 py-3">
                                  <p className="text-sm font-semibold text-white">{l.description || '—'}</p>
                                  <p className="mt-0.5 text-xs text-fieldiq-muted">
                                    {[l.rateCode, l.kindLabel, l.workDate ? wlDay(l.workDate) : null].filter(Boolean).join(' · ') || '—'}
                                  </p>
                                </td>
                                <td className="whitespace-nowrap px-4 py-3 text-right font-mono text-sm tabular-nums text-fieldiq-text" data-testid="wl-invoice-line-qty">{wlInvoiceQtyText(l)}</td>
                                <td className="whitespace-nowrap px-4 py-3 text-right font-mono text-sm tabular-nums text-fieldiq-text" data-testid="wl-invoice-line-rate">{wlRateText(l.rate, cur, l.unit, l.depthUnit, l.distanceUnit)}</td>
                                <td className="whitespace-nowrap px-4 py-3 text-right font-mono text-sm tabular-nums text-white" data-testid="wl-invoice-line-amount">{fmtCurrency(l.amount, cur)}</td>
                              </tr>
                            ))}
                          </tbody>
                        );
                      })}
                      <tfoot>
                        <tr className="border-t-2 border-l-4 border-fieldiq-border border-l-transparent">
                          <th scope="row" colSpan={3} className="px-4 py-2 text-right text-[0.64rem] font-black uppercase tracking-[0.12em] text-fieldiq-muted">Subtotal</th>
                          <td className="whitespace-nowrap px-4 py-2 text-right font-mono text-sm tabular-nums text-white" data-testid="wl-invoice-subtotal">{fmtCurrency(inv.subtotal, cur)}</td>
                        </tr>
                        {(() => {
                          const rows = wlLeviedTaxRows(inv);
                          return rows.length > 0 ? (
                            rows.map((r, i) => (
                              <tr key={i} className="border-l-4 border-l-transparent" data-testid="wl-invoice-tax-row">
                                <th scope="row" colSpan={3} className="px-4 py-2 text-right text-sm font-semibold text-fieldiq-text">{wlTaxRowLabel(r.label, r.percent)}</th>
                                <td className="whitespace-nowrap px-4 py-2 text-right font-mono text-sm tabular-nums text-white">{fmtCurrency(r.amount, cur)}</td>
                              </tr>
                            ))
                          ) : (
                            <tr className="border-l-4 border-l-transparent" data-testid="wl-invoice-tax-row">
                              <th scope="row" colSpan={3} className="px-4 py-2 text-right text-sm font-semibold text-fieldiq-text">Tax</th>
                              <td className="whitespace-nowrap px-4 py-2 text-right font-mono text-sm tabular-nums text-white">{fmtCurrency(inv.taxAmount, cur)}</td>
                            </tr>
                          );
                        })()}
                        <tr className="border-l-4 border-l-transparent">
                          <td colSpan={4} className="px-4 pb-2 text-right text-xs text-fieldiq-muted" data-testid="wl-invoice-tax-source">
                            Tax: {wlTaxSourceText(inv)}
                          </td>
                        </tr>
                        <tr className="border-t border-l-4 border-fieldiq-border border-l-transparent">
                          <th scope="row" colSpan={3} className="px-4 py-3 text-right text-[0.64rem] font-black uppercase tracking-[0.12em] text-fieldiq-muted">Total ({inv.currency || '—'})</th>
                          <td className="whitespace-nowrap px-4 py-3 text-right font-mono text-base font-extrabold tabular-nums text-white" data-testid="wl-invoice-total">{fmtCurrency(inv.total, cur)}</td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                )}
              </WlPanel>
            </>
          )}
        </div>
      </section>
    </div>
  );
}
